import styled from "@emotion/styled";
import type { StepColumnsType } from "@stepfinance/react";
import {
  ExtraBoldText,
  imageFromPlatformName,
  lightGrey2,
  lightGrey2Transp,
  StepAssetBreakdownPopoverChip,
  StepPopoverCellWrapper,
  StepTable,
  URLIcon,
} from "@stepfinance/react";
import { Col, Row } from "antd";
import type { TablePaginationConfig } from "antd/lib/table";
import * as numeral from "numeral";
import type { FC } from "react";
import { useEffect, useState } from "react";

import { ReactComponent as LightbulbSVG } from "../../../assets/lightbulb.svg";
import { DUST_THRESHOLD, useAppConfig } from "../../../contexts/app/config";
import { useLanguageContext } from "../../../contexts/language/language";
import { useManagedFundsContext } from "../../../contexts/managedFunds";
import { useMarkets } from "../../../contexts/market";
import type { CollapseControlProps } from "../../../hooks/home/useCollapseControl";
import { useCalculatePriceForDisplayCurrency } from "../../../hooks/utils";
import type { CurrentReturns, FundName, FundPosition, ManagedFundInvestment } from "../../../types/managedFunds";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { StyledTag } from "../../PercentageTag/PercentageTag";
import RedirectLink from "../../RedirectLink/RedirectLink";
import { IconTextContainer, PAGE_SIZE_OPTIONS, StepPopoverAssetWithIconRow, TokenIconText } from "../../shared";
import CellValue from "../../Table/CellValue";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";

const popoverContent = (composition: Array<FundPosition>) =>
  composition.map((item, i) => {
    return (
      <StepPopoverAssetWithIconRow
        tokenIconMintAddress={item.mint}
        amount={item.amount.toString()}
        symbol={item.symbol}
        key={`managed-funds-${i}`}
      />
    );
  });

export const ManagedFunds: FC<CollapseControlProps> = ({ activePanels, getPanelKey, onPanelClick }) => {
  const { managedFunds } = useManagedFundsContext();
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();
  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "stepManagedFundsPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );
  const { hideDust } = useAppConfig();
  const { priceInUSD } = useMarkets();

  const {
    currentLanguageData: {
      common,
      pages: {
        dashboard: {
          modules: { managedFunds: managedFundsText },
        },
      },
    },
  } = useLanguageContext();

  useEffect(() => {
    if (managedFunds?.investments.length) {
      setPagination({
        hideOnSinglePage: isPaginationHidden(managedFunds.investments.length, PAGE_SIZE_OPTIONS.m),
        showSizeChanger: true,
        pageSizeOptions: PAGE_SIZE_OPTIONS.m,
        pageSize: parseInt(paginationSetting),
      });
    }
  }, [managedFunds?.investments.length, paginationSetting]);

  const columns: StepColumnsType<ManagedFundInvestment> = [
    {
      title: common.platform,
      dataIndex: "platform",
      key: "platform",
      className: "column-string",
      render(value: string) {
        const image = imageFromPlatformName(value);
        return (
          <IconTextContainer>
            <URLIcon src={image ?? ""} />
            <TokenIconText>
              <ExtraBoldText>{value}</ExtraBoldText>
            </TokenIconText>
          </IconTextContainer>
        );
      },
      sorter: (a: ManagedFundInvestment, b: ManagedFundInvestment) => {
        return a.platform.localeCompare(b.platform);
      },
    },
    {
      title: managedFundsText.fundName,
      dataIndex: "fundName",
      key: "fundName",
      className: "column-string",
      render: (value: FundName) => {
        return (
          <RedirectLink href={value.href} title={value.name}>
            {value.name}
          </RedirectLink>
        );
      },
      sorter: (a: ManagedFundInvestment, b: ManagedFundInvestment) => {
        return a.fundName.name.localeCompare(b.fundName.name);
      },
    },
    {
      title: managedFundsText.fundPerformance,
      dataIndex: "returnOnInvestment",
      key: "returnOnInvestment",
      className: "column-number",
      render: (value: number | string) => {
        if (value === "Pending Deposit") {
          return (
            <StyledTag color={lightGrey2Transp} textColor={lightGrey2}>
              {value}
            </StyledTag>
          );
        } else if (typeof value === "number") {
          const formattedPercentage = numeral(value).format("0.00%");
          return <CellValue cellType="number">{formattedPercentage}</CellValue>;
        }
      },
      sorter: (a: ManagedFundInvestment, b: ManagedFundInvestment) => {
        const valueA = typeof a.returnOnInvestment === "number" ? a.returnOnInvestment : 0;
        const valueB = typeof b.returnOnInvestment === "number" ? b.returnOnInvestment : 0;

        return valueA - valueB;
      },
    },
    {
      title: common.value,
      dataIndex: "currentReturns",
      key: "currentReturns",
      className: "column-number",
      render: (value: CurrentReturns) => {
        const composition: FundPosition[] = value.composition.filter((item) => {
          if (hideDust) {
            const value = (priceInUSD(item.mint.toBase58()) ?? 0) * item.amount ?? 0;
            return value > DUST_THRESHOLD;
          }

          return item.amount > 0.000001;
        });

        return (
          <StepPopoverCellWrapper>
            <StepAssetBreakdownPopoverChip
              title={managedFundsText.fundComposition}
              assetValue={value.amount}
              assetValueDisplay={
                <CellValue cellType="number">{calculatePriceForDisplayCurrency(value.amount)}</CellValue>
              }
              content={popoverContent(composition)}
            />
          </StepPopoverCellWrapper>
        );
      },
      sorter: (a: ManagedFundInvestment, b: ManagedFundInvestment) => {
        return a.currentReturns.amount - b.currentReturns.amount;
      },
      defaultSortOrder: "descend",
    },
  ];

  return managedFunds.investments.length ? (
    <Row>
      <Col span={24}>
        <ChevronCollapseWrapper>
          <ChevronCollapse
            title={managedFundsText.title}
            value={managedFunds.sumReturns}
            TitleIcon={LightbulbSVG}
            className="padded-collapse-content"
            activePanels={activePanels}
            panelKey={getPanelKey("ManagedFunds")}
            onChange={onPanelClick}
          >
            <StepTable
              className="managed-funds-table dashboard-table"
              dataSource={managedFunds.investments}
              pagination={pagination}
              columns={columns}
              rowKey={(record: ManagedFundInvestment) => record.key}
              onChange={(pagination) => {
                setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
              }}
            />
          </ChevronCollapse>
        </ChevronCollapseWrapper>
      </Col>
    </Row>
  ) : null;
};

const ChevronCollapseWrapper = styled.div`
  margin-top: 20px;
`;
